import {
  FC,
  FormEvent,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from "react";
import { Dialog, DialogPanel } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { AddCustomer } from "api/customer/customer";
import ButtonPrimary from "shared/Button/ButtonPrimary";

/**
 * 1NOW-10770: a returning renter's email at checkout needs the code we mail them.
 *
 * It is the add-website-customer call again, not a new endpoint. With `email_code: true` a renter who has booked
 * with this company before gets no session but `{ code_required: true, detail, resend_after }` and a six-digit code
 * by email; the same body plus `code` answers with the full session. A new renter, or an email with no booking
 * here, gets the session straight away and never sees this box.
 */

const CODE_LENGTH = 6;
const WRONG = "That code isn't right or has expired.";

/** What AddCustomer was sent: the renter's details and the company. */
type Customer = Record<string, any>;

/** Pay now opens the checkout once the code is right; pay later reserves the car. */
type Mode = "now" | "later";

interface Gate {
  mode: Mode;
  customer: Customer;
  /** Seconds until another code may be asked for, as the backend said. */
  resendAfter?: number;
  /** Why no code works yet (asked again too soon), shown in the box from the start. */
  error?: string;
}

/** AddCustomer's answer is the code question (a returning renter's email): no session in it. */
export const codeRequired = (data: any): boolean => data?.code_required === true;

/** "sara.khan@email.com" as "s***@email.com": enough to tell the renter which inbox, not enough to read it. */
const maskEmail = (email: string) => {
  const [local, domain] = String(email ?? "").trim().split("@");
  return local && domain ? `${local.charAt(0)}***@${domain}` : "";
};

/** 125 gives "2:05". */
const clock = (s: number) =>
  `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

const cleanCode = (value: string) =>
  String(value ?? "").replace(/\D/g, "").slice(0, CODE_LENGTH);

/**
 * One code question on a car page. The page calls `ask` when AddCustomer answers `code_required`, and passes the
 * hook to <EmailCodeModal>. `known` hands back the renter a right code already proved for these very details: a
 * code works once, so a booking that fails after it is retried with that session, not with another code.
 */
export const useEmailCode = () => {
  const [gate, setGate] = useState<Gate | null>(null);
  const verified = useRef<{ key: string; data: any } | null>(null);
  return {
    gate,
    close: () => setGate(null),
    ask: (mode: Mode, customer: Customer, data: any) =>
      setGate({
        mode,
        customer,
        resendAfter: Number(data?.resend_after) || undefined,
        error: data?.error,
      }),
    known: (customer: Customer) =>
      verified.current?.key === JSON.stringify(customer)
        ? verified.current.data
        : null,
    forget: () => {
      verified.current = null;
      setGate(null);
    },
    /** Six digits typed: the session is kept, or the server's own words come back. */
    verify: async (code: string): Promise<string | null> => {
      if (!gate) return WRONG;
      const r = await AddCustomer({ ...gate.customer, email_code: true, code });
      if (r.error) return r.error;
      // A right code is answered with the session, never with another request for a code.
      if (codeRequired(r.data)) return r.data.error ?? WRONG;
      verified.current = { key: JSON.stringify(gate.customer), data: r.data };
      return null;
    },
    /** "Resend code" is the same call without `code`: a new mail once the wait is over. */
    resend: async (): Promise<
      { resendAfter?: number; notice: string } | { error: string; resendAfter?: number }
    > => {
      if (!gate) return { error: WRONG };
      const r = await AddCustomer({ ...gate.customer, email_code: true });
      if (r.error) return { error: r.error };
      const wait = Number(r.data?.resend_after) || undefined;
      if (r.data?.error) return { error: r.data.error, resendAfter: wait };
      return {
        resendAfter: wait,
        notice: r.data?.detail ?? "We sent you a new code.",
      };
    },
  };
};

/**
 * Six boxes, OTP style. One real input lies over them (invisible), so typing, Backspace, paste and the phone's
 * one-time-code autofill all work as on any field; the boxes only draw it, the next one highlighted.
 */
const CodeBoxes = forwardRef<
  HTMLInputElement,
  { value: string; onChange: (next: string) => void; invalid?: boolean }
>(({ value, onChange, invalid }, ref) => {
  const [focused, setFocused] = useState(false);
  return (
    <>
      <label
        className="mb-1.5 block text-sm font-semibold text-text-color"
        htmlFor="email-code"
      >
        6-digit code
      </label>
      <div className="relative w-full max-w-[22rem]">
        <div className="grid grid-cols-6 gap-2" aria-hidden="true">
          {Array.from({ length: CODE_LENGTH }, (_, i) => {
            const active =
              focused && i === Math.min(value.length, CODE_LENGTH - 1);
            return (
              <span
                key={i}
                className={`flex h-12 items-center justify-center rounded-xl border bg-white text-xl font-bold text-text-color ${
                  invalid
                    ? "border-red-500"
                    : active
                    ? "border-primary-300 ring ring-primary-200 ring-opacity-50"
                    : "border-neutral-200"
                }`}
              >
                {value[i] ?? ""}
              </span>
            );
          })}
        </div>
        <input
          id="email-code"
          name="code"
          ref={ref}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={CODE_LENGTH}
          data-autofocus
          value={value}
          onChange={(e) => onChange(cleanCode(e.target.value))}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          aria-invalid={!!invalid}
          className="absolute inset-0 h-full w-full cursor-text border-0 bg-transparent text-transparent caret-transparent opacity-0 focus:ring-0"
        />
      </div>
    </>
  );
});
CodeBoxes.displayName = "CodeBoxes";

const linkCls =
  "text-sm font-semibold text-text-color underline disabled:no-underline disabled:opacity-60";

/**
 * "Check your email": a dialog over the car page, opened when AddCustomer asked for a code. `onVerified` is what a
 * right code does (carry on with the booking, with the session the hook kept); the dialog closes when it is done.
 * "Use a different email" closes it and calls `onDifferentEmail`, which empties and focuses the email field.
 */
const EmailCodeModal: FC<{
  code: ReturnType<typeof useEmailCode>;
  onVerified: (mode: Mode) => Promise<unknown>;
  onDifferentEmail: () => void;
}> = ({ code, onVerified, onDifferentEmail }) => {
  const { gate } = code;
  if (!gate) return null;
  return (
    <Box
      key={JSON.stringify(gate.customer)}
      gate={gate}
      code={code}
      onVerified={onVerified}
      onDifferentEmail={onDifferentEmail}
    />
  );
};

const Box: FC<{
  gate: Gate;
  code: ReturnType<typeof useEmailCode>;
  onVerified: (mode: Mode) => Promise<unknown>;
  onDifferentEmail: () => void;
}> = ({ gate, code, onVerified, onDifferentEmail }) => {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(gate.error ?? "");
  const [bad, setBad] = useState(false);
  const [notice, setNotice] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(gate.resendAfter ?? 0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // The resend wait counts down while the box is open.
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft]);

  const verify = async (digits: string) => {
    if (busy) return;
    if (digits.length !== CODE_LENGTH) {
      setProblem(`Enter the ${CODE_LENGTH}-digit code.`);
      return;
    }
    setProblem("");
    setNotice("");
    setBad(false);
    setBusy(true);
    const refused = await code.verify(digits);
    if (refused) {
      setBusy(false);
      setProblem(refused);
      setBad(true);
      return;
    }
    // The booking is made with the session the right code returned; its own messages show as toasts.
    await onVerified(gate.mode);
    code.close();
  };

  const resend = async () => {
    if (busy || secondsLeft > 0) return;
    setProblem("");
    setBad(false);
    setNotice("");
    setBusy(true);
    const r = await code.resend();
    setBusy(false);
    if ("error" in r) {
      setProblem(r.error);
      if (r.resendAfter) setSecondsLeft(r.resendAfter);
    } else {
      setValue("");
      setSecondsLeft(r.resendAfter ?? 60);
      setNotice(r.notice);
    }
  };

  return (
    <Dialog
      as="div"
      open
      onClose={() => !busy && code.close()}
      aria-label="Check your email"
      className="relative z-50"
    >
      <div className="fixed inset-0 bg-black/55" aria-hidden="true" />
      <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="relative w-full max-w-[460px] rounded-2xl border border-neutral-200 bg-bg-color p-6 shadow-2xl">
            <button
              type="button"
              onClick={code.close}
              disabled={busy}
              aria-label="Close"
              className="absolute right-4 top-4 text-text-color opacity-70 hover:opacity-100"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
            <form
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                void verify(value);
              }}
              noValidate
              className="text-text-color"
              data-testid="email-code"
            >
              <h2 className="text-xl font-bold">Check your email</h2>
              <p className="mb-4 mt-1 text-sm leading-relaxed text-text-color opacity-70">
                You&apos;ve booked with us before. Enter the code we sent to{" "}
                <span className="font-bold">
                  {maskEmail(gate.customer.email)}
                </span>
                . It works for 10 minutes.
              </p>
              {problem && (
                <div
                  role="alert"
                  className="mb-4 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700"
                >
                  {problem}
                </div>
              )}
              {notice && !problem && (
                <p role="status" className="mb-4 text-sm font-semibold">
                  {notice}
                </p>
              )}
              <CodeBoxes
                ref={inputRef}
                value={value}
                invalid={bad}
                onChange={(next) => {
                  setValue(next);
                  if (bad) setBad(false);
                  if (next.length === CODE_LENGTH) void verify(next);
                }}
              />
              <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
                <ButtonPrimary
                  type="submit"
                  className="rounded-lg"
                  disabled={busy}
                  loading={busy}
                >
                  Continue
                </ButtonPrimary>
                <button
                  type="button"
                  onClick={resend}
                  disabled={busy || secondsLeft > 0}
                  className={linkCls}
                >
                  {secondsLeft > 0
                    ? `Resend code in ${clock(secondsLeft)}`
                    : "Resend code"}
                </button>
              </div>
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  code.forget();
                  onDifferentEmail();
                }}
                className={`mt-4 ${linkCls}`}
              >
                Use a different email
              </button>
              <p className="mt-4 text-sm text-text-color opacity-70">
                {gate.mode === "now"
                  ? "Payment opens once your code is confirmed."
                  : "Your car is not reserved yet. We reserve it as soon as the code is confirmed."}
              </p>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default EmailCodeModal;
